import { useMemo } from 'react';
import {
  SOFT_LIMIT_NOTE,
  hardLimitForField,
  softDisplayLimitForField,
  visibleLength,
  type EnrichableField,
} from '@vce/shared';
import { diffWords } from '@/lib/diff';
import {
  displayValueForField,
  parseSpecSuggestionPayloadLoose,
  serializeSpecSuggestionPayload,
  valuesFromEditableText,
} from '@/lib/spec-suggestion-display';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/primitives';

export function DiffPanel({
  before,
  after,
  side,
  field,
}: {
  before: string;
  after: string;
  side: 'before' | 'after';
  /** Quando `specifications`, valores JSON são exibidos como `Nome: valores`. */
  field?: string;
}) {
  const displayBefore = useMemo(
    () => (field ? displayValueForField(field, before) : before),
    [field, before],
  );
  const displayAfter = useMemo(
    () => (field ? displayValueForField(field, after) : after),
    [field, after],
  );
  const tokens = useMemo(() => diffWords(displayBefore, displayAfter), [displayBefore, displayAfter]);
  const visible = tokens.filter((token) =>
    side === 'before' ? token.op !== 'insert' : token.op !== 'delete',
  );

  if (visible.length === 0) {
    return (
      <p className="diff-pane text-xs italic text-[var(--color-muted)]">
        {side === 'before' ? 'Vazio no snapshot da geração.' : 'Sem valor sugerido.'}
      </p>
    );
  }

  return (
    <p className="diff-pane whitespace-pre-wrap text-sm leading-relaxed text-[var(--color-body)]">
      {visible.map((token, index) => (
        <span
          key={index}
          className={cn(
            token.op === 'delete' &&
              'rounded bg-[color-mix(in_srgb,var(--color-error)_22%,transparent)] text-[var(--color-error)] line-through decoration-[var(--color-error)]',
            token.op === 'insert' &&
              'rounded bg-[color-mix(in_srgb,var(--color-success)_22%,transparent)] text-[var(--color-success)]',
          )}
        >
          {token.value}
        </span>
      ))}
    </p>
  );
}

/**
 * Contador de caracteres com dois patamares: o limite duro (a VTEX ou o
 * Merchant Center rejeitam acima dele) e a convenção de SERP, que só avisa.
 * Conta o texto visível, não o HTML, porque é isso que o usuário lê.
 */
export function CharacterCounter({ field, value }: { field: string; value: string }) {
  const length = visibleLength(value);
  const hard = hardLimitForField(field as EnrichableField);
  const soft = softDisplayLimitForField(field as EnrichableField);

  const overHard = hard !== undefined && length > hard;
  const overSoft = !overHard && soft !== undefined && length > soft;

  return (
    <span
      className={cn(
        'text-[11px] tabular-nums',
        overHard && 'font-medium text-[var(--color-error)]',
        overSoft && 'text-[var(--color-warning)]',
        !overHard && !overSoft && 'text-[var(--color-muted)]',
      )}
      title={overSoft ? SOFT_LIMIT_NOTE : undefined}
    >
      {length}
      {hard !== undefined ? ` / ${hard}` : ''}
      {overHard ? ' · acima do limite da VTEX' : ''}
      {overSoft ? ` · acima de ${soft ?? 0} (convenção de SERP)` : ''}
    </span>
  );
}

/** Editor de spec quando o suggestedValue é JSON válido; senão textarea de JSON bruto. */
export function SpecSuggestionValueEditor({
  draft,
  onDraftChange,
}: {
  draft: string;
  onDraftChange(next: string): void;
}) {
  const payload = parseSpecSuggestionPayloadLoose(draft);

  if (!payload) {
    return (
      <div className="space-y-2">
        <label className="text-xs font-medium text-[var(--color-muted)]">JSON da especificação</label>
        <Textarea rows={12} value={draft} autoFocus onChange={(event) => onDraftChange(event.target.value)} />
      </div>
    );
  }

  const valueText = payload.values.join(', ');

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-[var(--color-muted)]">Nome</label>
        <input
          readOnly
          value={payload.name}
          className="w-full rounded-[10px] border border-[var(--color-hairline)] bg-[var(--color-surface-elevated)] px-3.5 py-2.5 text-sm text-[var(--color-muted)]"
        />
      </div>
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-[var(--color-muted)]">Valor</label>
        <Textarea
          rows={6}
          autoFocus
          value={valueText}
          onChange={(event) => {
            const values = valuesFromEditableText(event.target.value);
            onDraftChange(
              serializeSpecSuggestionPayload({
                ...payload,
                values: values.length > 0 ? values : [''],
              }),
            );
          }}
        />
        <p className="text-[11px] text-[var(--color-muted)]">
          Vários valores: separe com vírgula. O JSON completo é remontado ao salvar.
        </p>
      </div>
    </div>
  );
}
